import Phaser from "phaser";
import { ENEMIES, TOWERS } from "../config";
import { Enemy } from "../entities/Enemy";
import { Projectile } from "../entities/Projectile";
import { Tower } from "../entities/Tower";
import { buildMapFromDef, MapData } from "../map/MapData";
import { MapRenderer } from "../map/MapRenderer";
import { CAMPAIGN_MAPS, findMap, MapDef } from "../maps/maps";
import { Economy } from "../systems/Economy";
import { AbilityController } from "../systems/AbilityController";
import { BossController } from "../systems/BossController";
import { save } from "../systems/Save";
import { TowerPlacement } from "../systems/TowerPlacement";
import { WaveManager } from "../systems/WaveManager";
import { submitRun } from "../backend/runSubmit";
import { computeScore } from "../systems/score";
import { achievements } from "../systems/Achievements";
import { quests } from "../systems/Quests";
import { sound } from "../systems/Sound";
import { fx } from "../systems/FxPool";
import { postFx } from "../systems/PostFx";
import { atmosphere } from "../systems/Atmosphere";
import { impact } from "../systems/Impact";
import { portal } from "../portal/portal";

export class GameScene extends Phaser.Scene {
  private map!: MapData;
  private mapRenderer!: MapRenderer;
  private mapDef!: MapDef;
  private economy!: Economy;
  private waveManager!: WaveManager;
  private placement!: TowerPlacement;
  private bossCtl!: BossController;
  private abilityCtl!: AbilityController;
  private difficulty: number = 1;

  private enemies: Enemy[] = [];
  private projectiles: Projectile[] = [];
  private towers: Tower[] = [];

  private gameOver = false;
  private speed = 1;

  // Run telemetry — submitted to leaderboard backend on game over.
  private runSeed = 0;
  private runStartMs = 0;
  private wavesClearedCount = 0;
  private totalLeaks = 0;
  private campaignClearRecorded = false;
  private cleanupFns: Array<() => void> = [];

  static readonly SPEED_OPTIONS = [1, 2, 4] as const;

  constructor() { super("GameScene"); }

  create(): void {
    this.runCleanup();
    this.cleanupFns = [];
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.runCleanup());

    this.enemies = [];
    this.projectiles = [];
    this.towers = [];
    this.gameOver = false;
    this.speed = 1;

    this.runSeed = Math.floor(Math.random() * 2_147_483_647);
    this.runStartMs = Date.now();
    this.wavesClearedCount = 0;
    this.totalLeaks = 0;
    this.campaignClearRecorded = false;

    const mapKey = (this.registry.get("selectedMap") as string | undefined) ?? CAMPAIGN_MAPS[0].key;
    const def = findMap(mapKey) ?? CAMPAIGN_MAPS[0];
    this.mapDef = def;
    this.difficulty = (this.registry.get("selectedDifficulty") as number | undefined) ?? 1;
    const hpMult = this.difficulty === 3 ? 1.9 : this.difficulty === 2 ? 1.4 : 1.0;
    const rewardMult = this.difficulty === 3 ? 1.3 : this.difficulty === 2 ? 1.15 : 1.0;
    // Pool must bind before anything can spawn an effect.
    fx.attach(this);
    this.map = buildMapFromDef(def);
    // Use the same per-map painting shown in level select as the battlefield
    // base. MapData remains authoritative for pathing and placement; the
    // raster only replaces the visibly repetitive tile composition.
    const paintedMapKey = def.backgroundImage ?? `map-preview-${def.key}`;
    this.mapRenderer = new MapRenderer(this, this.map, def.biome, paintedMapKey);
    this.mapRenderer.draw();
    // Painted maps already contain their final road. Their authored route is
    // projected into MapData, so no second road needs to be drawn on top.
    if (!this.mapRenderer.hasBackgroundImage()) this.drawPathOverlay();

    // Cinematic layer: graded frame + biome weather. Both key off the map's
    // biome so a snow map reads cold and a mine map reads like torchlight.
    postFx.attach(this, def.biome);
    atmosphere.attach(this, def.biome);
    impact.attach(this);

    this.economy = new Economy(def.startGold, def.castleHp);

    save.load();
    save.recordRunStart();

    this.waveManager = new WaveManager(
      this,
      this.map.waypoints,
      (e) => this.enemies.push(e),
      () => this.enemies.filter(e => e.alive),
      def.waves,
      this.map.altWaypoints,
      hpMult,
      rewardMult,
      // Biome doubles as the wave theme, so generated waves and the Knight/Lord
      // escalation both draw from this map's own enemy roster.
      def.biome,
      this.difficulty
    );

    // Award XP each cleared wave; emit a scene event so UI can flash a level-up.
    this.cleanupFns.push(this.waveManager.on((ev) => {
      if (ev.kind === "wave-start") {
        // Boss waves: layer a low drone over the meadow loop. Plain waves
        // remove it. Music itself never restarts.
        sound.setBossLayer(ev.boss);
        // ...and thicken the weather so the field visibly darkens as the boss
        // arrives, then eases back once the wave is plain again.
        atmosphere.setIntensity(ev.boss ? 1.55 : 1);
        portal.gameplayStart();
      }
      if (ev.kind === "wave-cleared") {
        this.wavesClearedCount = ev.index + 1;
        const bonus = this.economy.applyWaveBonus(ev.index + 1);
        const xpMult = this.difficulty === 3 ? 3 : this.difficulty === 2 ? 1.5 : 1;
        const levels = save.recordWaveCleared(ev.index + 1, xpMult);
        if (levels > 0) this.events.emit("player-level-up", { levels, save: save.get() });
        this.events.emit("player-xp-gained", { save: save.get(), bonus });
        // The campaign ends on this map's own boss wave — the last entry of its
        // authored script. Everything past it is opt-in Endless.
        const campaignWaves = this.waveManager.totalWaves;
        if (this.wavesClearedCount === campaignWaves) {
          this.waveManager.setAutoContinue(false);
          this.recordCampaignClear();
          // Clearing this map unlocks the next one, so the HUD can name it.
          const mapIdx = CAMPAIGN_MAPS.findIndex(m => m.key === this.mapDef.key);
          const nextMap = mapIdx >= 0 ? CAMPAIGN_MAPS[mapIdx + 1] : undefined;
          this.events.emit("campaign-secured", {
            wave: this.wavesClearedCount,
            mapName: this.mapDef.name,
            nextMapName: nextMap?.name ?? null,
          });
        } else if (this.wavesClearedCount > campaignWaves && this.wavesClearedCount % 5 === 0) {
          const endlessGold = 180 + Math.floor((this.wavesClearedCount - campaignWaves) * 18);
          const endlessXp = Math.round((260 + this.wavesClearedCount * 12) * xpMult);
          this.economy.grantGold(endlessGold);
          const extraLevels = save.addXp(endlessXp);
          if (extraLevels > 0) this.events.emit("player-level-up", { levels: extraLevels, save: save.get() });
          this.events.emit("endless-milestone", { wave: this.wavesClearedCount, gold: endlessGold, xp: endlessXp });
        }
      }
      if (ev.kind === "all-cleared") {
        this.recordCampaignClear();
        if (!this.gameOver) this.endGame(true);
      }
    }));

    this.placement = new TowerPlacement(
      this, this.map, this.economy,
      this.towers,
      () => this.enemies,
      (p) => this.projectiles.push(p)
    );
    this.cleanupFns.push(() => this.placement.destroy());

    this.bossCtl = new BossController(this, this.map.waypoints, (e) => this.enemies.push(e));
    this.bossCtl.onTrackedChanged((boss) => this.events.emit("boss-tracked", boss));

    this.abilityCtl = new AbilityController(
      this,
      () => this.towers,
      () => this.enemies,
      (n) => this.economy.healLives(n),
      (n) => this.economy.grantGold(n)
    );
    this.registry.set("abilityController", this.abilityCtl);

    const onKey = (event: string, fn: () => void) => {
      this.input.keyboard?.on(event, fn);
      this.cleanupFns.push(() => this.input.keyboard?.off(event, fn));
    };

    onKey("keydown-Q", () => this.abilityCtl.cast("rally"));
    onKey("keydown-W", () => this.abilityCtl.cast("meteor", this.input.activePointer));
    onKey("keydown-E", () => this.abilityCtl.cast("heal"));
    onKey("keydown-R", () => this.abilityCtl.cast("conscript"));

    // Split-on-death: spawn children where the parent died, continuing along the parent's path.
    const onEnemySplit = (p: { type: string; count: number; x: number; y: number; waypointIdx: number; waypoints: { x: number; y: number }[] }) => {
      const childStats = ENEMIES[p.type];
      if (!childStats) return;
      for (let i = 0; i < p.count; i++) {
        const child = new Enemy(this, childStats, p.waypoints);
        child.x = p.x + (Math.random() - 0.5) * 16;
        child.y = p.y + (Math.random() - 0.5) * 16;
        child.waypointIdx = p.waypointIdx;
        this.enemies.push(child);
      }
    };
    this.events.on("enemy:split", onEnemySplit);
    this.cleanupFns.push(() => this.events.off("enemy:split", onEnemySplit));

    this.registry.set("economy", this.economy);
    this.registry.set("waveManager", this.waveManager);
    this.registry.set("placement", this.placement);
    this.registry.set("mapData", this.map);

    // Clear the cosmetic biome overlay (e.g. pine tree) on the cell where a
    // tower was just placed so the tower doesn't visually sit on top of trees.
    const onTowerPlaced = (p: { c: number; r: number }) => {
      this.mapRenderer.clearOverlayAt(p.c, p.r);
    };
    this.events.on("tower:placed", onTowerPlaced);
    this.cleanupFns.push(() => this.events.off("tower:placed", onTowerPlaced));

    // Hotkeys 1..8 to choose towers (gated by Player Level)
    const tryHotkey = (key: keyof typeof TOWERS) => {
      const t = TOWERS[key];
      const required = t.unlockLevel ?? 1;
      if (save.get().playerLevel < required) {
        this.events.emit("hotkey-locked", { tower: t, required });
        return;
      }
      this.placement.select(t);
    };
    onKey("keydown-ONE",   () => tryHotkey("archer"));
    onKey("keydown-TWO",   () => tryHotkey("cannon"));
    onKey("keydown-THREE", () => tryHotkey("mage"));
    onKey("keydown-FOUR",  () => tryHotkey("frost"));
    onKey("keydown-FIVE",  () => tryHotkey("lightning"));
    onKey("keydown-SIX",   () => tryHotkey("poison"));
    onKey("keydown-SEVEN", () => tryHotkey("crossbow"));
    onKey("keydown-EIGHT", () => tryHotkey("holy"));

    // Calm gameplay loop. Two interchangeable tracks; pick one per run so the
    // user doesn't always hear the same theme. Boss waves layer a drone on top.
    sound.attach(this);
    sound.setBossLayer(false);
    sound.playMusic(Math.random() < 0.5 ? "meadowA" : "meadowB", { fadeMs: 1500 });
    sound.playAmbience("wind", 0.25);
    sound.playAmbience("fire", 0.15);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => sound.stopAmbience());

    this.events.emit("game-ready");
  }

  private runCleanup(): void {
    const fns = this.cleanupFns.splice(0);
    for (const fn of fns) {
      try { fn(); } catch { /* ignore stale cleanup */ }
    }
  }

  private drawPathOverlay(): void {
    const g = this.add.graphics().setDepth(-5);
    g.lineStyle(2, 0xffffff, 0.15);
    const drawPath = (wps: { x: number; y: number }[]) => {
      g.beginPath();
      g.moveTo(wps[0].x, wps[0].y);
      for (let i = 1; i < wps.length; i++) g.lineTo(wps[i].x, wps[i].y);
      g.strokePath();
    };
    drawPath(this.map.waypoints);
    if (this.map.altWaypoints) drawPath(this.map.altWaypoints);
  }

  update(time: number, delta: number): void {
    // The impact director defines scaled time, so it runs on the real delta
    // and its output feeds everything else. timeScale is 0 during hit-stop.
    impact.update(delta);
    const d = delta * this.speed * impact.timeScale;

    // Pooled VFX integrate on the scaled delta, so impacts stay in sync with
    // the action at 2x/4x instead of trailing behind in real time — and they
    // freeze with the world during a hit-stop. Ticked before the game-over
    // guard so the final burst still fades out instead of freezing mid-air.
    fx.update(d);
    // Presentation layers run on the REAL delta: grain and weather shouldn't
    // sprint just because the player pressed 4x, and they must keep breathing
    // through a hit-stop or the freeze reads as a crash.
    postFx.update(delta);
    atmosphere.update(delta);

    if (this.gameOver) return;

    this.waveManager.update(time, d);

    this.bossCtl.update(time, d, this.enemies);
    this.abilityCtl.update(time, d);

    for (const t of this.towers) t.update(time, d);

    for (const e of this.enemies) {
      if (!e.alive && !e.reachedEnd) continue;
      e.update(time, d);
    }

    for (const p of this.projectiles) p.update(time, d);

    // Cleanup dead enemies, award gold/lives
    for (const e of this.enemies) {
      if (e.alive) continue;
      if (e.reachedEnd) {
        this.economy.loseLives(e.stats.damage);
        this.totalLeaks += e.stats.damage;
        // Polish: bigger bloom for boss leakers, smaller for trash.
        const isBoss = !!(e.stats as { boss?: boolean }).boss;
        // Graded red bloom through the post pass reads far heavier than the
        // old flat camera flash, and it respects the biome grade.
        postFx.flash(0.85, 0.12, 0.10, isBoss ? 0.5 : 0.3, isBoss ? 420 : 260);
      } else {
        this.economy.reward(e.stats.reward);
        save.recordEnemyKilled(e.stats.key, !!(e.stats as { boss?: boolean }).boss);
      }
      e.destroy();
    }
    this.enemies = this.enemies.filter(e => e.alive);
    this.projectiles = this.projectiles.filter(p => p.alive);

    if (this.economy.isDead && !this.gameOver) this.endGame(false);
  }

  endGame(victory: boolean): void {
    // Both the simulation and HUD observe `all-cleared`. This guard keeps a
    // repeated event from creating duplicate overlays/submissions during a
    // scene transition.
    if (this.gameOver) return;
    this.gameOver = true;
    // Clearing the map's boss wave is what wins the campaign; dying later in
    // Endless ends the session but does not undo that. The overlay and audio
    // below report how this session ended, the leaderboard reports the campaign
    // result — otherwise every Endless run would submit as a loss.
    const campaignWon = victory || this.campaignClearRecorded;
    portal.gameplayStop();
    // Polish: dramatic graded flash to punctuate run end.
    if (victory) postFx.flash(1.0, 0.92, 0.55, 0.62, 900);
    else         postFx.flash(0.75, 0.10, 0.10, 0.66, 900);
    atmosphere.setIntensity(victory ? 0.7 : 1.8);
    // Audio: drop boss layer; on victory crossfade into a soft warm sting,
    // on defeat into the somber lute stinger. playMusic handles the crossfade
    // itself, so no stop+delay+play (which produced an audible silence gap).
    sound.setBossLayer(false);
    sound.playMusic(victory ? "victory" : "defeat", { fadeMs: 1400, loop: false });
    this.events.emit("game-over", { victory });

    // Notify achievement system with run stats.
    if (victory) this.recordCampaignClear();
    const towerKeys = new Set(this.towers.map(t => t.stats.key));
    const runStats = {
      mapKey:           this.mapDef.key,
      difficulty:       this.difficulty,
      victory,
      leaks:            this.totalLeaks,
      durationMs:       Date.now() - this.runStartMs,
      uniqueTowerTypes: towerKeys.size,
      towersOnField:    this.towers.length,
      onlyTowerKey:     towerKeys.size === 1 ? [...towerKeys][0] : null,
    };
    achievements.notifyRunEnd(runStats);
    quests.notifyRunEnd(runStats);

    // Submit run to leaderboard backend (no-op when not signed in).
    const score = computeScore({
      victory: campaignWon,
      wavesCleared: this.wavesClearedCount,
      castleHp: this.mapDef.castleHp,
      leaks: this.totalLeaks,
      difficulty: this.difficulty,
    });
    void submitRun({
      mapKey:        this.mapDef.key,
      difficulty:    this.difficulty,
      outcome:       campaignWon ? "win" : "loss",
      wavesCleared:  this.wavesClearedCount,
      leaks:         this.totalLeaks,
      durationMs:    Date.now() - this.runStartMs,
      score,
      seed:          this.runSeed,
    });
  }

  private recordCampaignClear(): void {
    if (this.campaignClearRecorded) return;
    this.campaignClearRecorded = true;
    save.recordRunWon(this.mapDef.key, this.difficulty);
    const towerKeys = new Set(this.towers.map(t => t.stats.key));
    const runStats = {
      mapKey:           this.mapDef.key,
      difficulty:       this.difficulty,
      victory:          true,
      leaks:            this.totalLeaks,
      durationMs:       Date.now() - this.runStartMs,
      uniqueTowerTypes: towerKeys.size,
      towersOnField:    this.towers.length,
      onlyTowerKey:     towerKeys.size === 1 ? [...towerKeys][0] : null,
    };
    achievements.notifyRunEnd(runStats);
    quests.notifyRunEnd(runStats);
  }

  getSpeed(): number { return this.speed; }

  setSpeed(mult: number): void {
    this.speed = mult;
    this.events.emit("speed-changed", mult);
  }

  cycleSpeed(): number {
    const opts = GameScene.SPEED_OPTIONS;
    const idx = opts.indexOf(this.speed as 1 | 2 | 4);
    const next = opts[(idx + 1) % opts.length];
    this.setSpeed(next);
    return next;
  }

  restart(): void {
    // Restart cleanly via the SceneManager. Driving stop/start through the
    // game's SceneManager (rather than this scene's plugin) avoids the no-op
    // / queueing race that happens when GameScene asks itself to restart from
    // inside a UIScene pointer handler. We defer one tick so the originating
    // pointer event finishes propagating before scenes are torn down.
    sound.setBossLayer(false);
    const mgr = this.scene.manager;
    setTimeout(() => {
      mgr.stop("UIScene");
      mgr.stop("GameScene");
      mgr.start("GameScene");
      mgr.start("UIScene");
    }, 0);
  }

  quitToMenu(): void {
    // Tear down the running game + HUD and return to the main menu.
    // Deferred one tick for the same reason as restart() — keeps pointer
    // handlers from racing the SceneManager teardown.
    portal.gameplayStop();
    sound.setBossLayer(false);
    const mgr = this.scene.manager;
    setTimeout(() => {
      mgr.stop("UIScene");
      mgr.stop("GameScene");
      mgr.start("MenuScene");
    }, 0);
  }

  quitToMapSelect(): void {
    // Same teardown as quitToMenu, but lands on the atlas — where a freshly
    // unlocked map is what the player wants to see after securing this one.
    portal.gameplayStop();
    sound.setBossLayer(false);
    const mgr = this.scene.manager;
    setTimeout(() => {
      mgr.stop("UIScene");
      mgr.stop("GameScene");
      mgr.start("MapSelectScene");
    }, 0);
  }

  /** Return to MapEditorScene after a PLAY TEST run, restoring the map + mode it was launched from. */
  quitToEditor(): void {
    portal.gameplayStop();
    sound.setBossLayer(false);
    const mapIndex = (this.registry.get("editorMapIndex") as number | undefined) ?? 0;
    const mode = (this.registry.get("editorMode") as "mask" | "path" | "foliage" | undefined) ?? "mask";
    this.registry.set("editorPlayTest", false);
    const mgr = this.scene.manager;
    setTimeout(() => {
      mgr.stop("UIScene");
      mgr.stop("GameScene");
      mgr.start("MapEditorScene", { mapIndex, mode });
    }, 0);
  }
}
